import React from "react";
import { Link } from "react-router";
import { ScrollText } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useDocsBasePath, useReleaseForVersion } from "../../hooks/useDocs";
import { releaseAnchor } from "../../models/Docs";

interface ReleaseNotesLinkProps {
  component: "client" | "updater";
  version?: string;
  /** Icon only, for tight table cells. */
  compact?: boolean;
  className?: string;
}

/**
 * Links a client or updater build to the release notes of the prod release that shipped it. Renders nothing for
 * builds no release lists — dev-channel, custom and pre-changelog builds.
 */
const ReleaseNotesLink: React.FC<ReleaseNotesLinkProps> = ({ component, version, compact, className }) => {
  const findRelease = useReleaseForVersion();
  const docsBase = useDocsBasePath();
  const release = findRelease(component, version);
  if (!release) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={`${docsBase}/changelog#${releaseAnchor(release.id)}`}
          className={cn("inline-flex items-center gap-1 text-xs text-primary hover:underline underline-offset-4", className)}
          aria-label={`What changed in ${version}`}
        >
          <ScrollText className="size-3.5" />
          {!compact && "What changed"}
        </Link>
      </TooltipTrigger>
      <TooltipContent>Release notes for the {release.id} release</TooltipContent>
    </Tooltip>
  );
};

export default ReleaseNotesLink;
